import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { ReportTabActions } from '../index';
import { IBaseTab, ITabDashboard, ITabInfo, ITabReport } from '../model/report-tabs.model';

export interface ReportTabState extends EntityState<IBaseTab<ITabReport | ITabDashboard>> {
    selectedReportTabId: string | number;
    tabInfo: { [k: string]: ITabInfo };
}

export const REPORT_TABS_FEATURE_KEY = 'tabs';

export const reportTabAdapter = createEntityAdapter<IBaseTab<ITabReport | ITabDashboard>>({
    selectId: (tab: IBaseTab<ITabReport | ITabDashboard>) => tab.tabId,
    sortComparer: (tabA: IBaseTab<ITabReport | ITabDashboard>, tabB: IBaseTab<ITabReport | ITabDashboard>) =>
        tabA.tabId === tabB.tabId ? 0
        : tabA.tabId > tabB.tabId ? 1
        : -1
});

export const reportTabsInitialState: ReportTabState = reportTabAdapter.getInitialState({
    selectedReportTabId: -1,
    tabInfo: {}
});

function calculateTabIndex(state: ReportTabState, tab: IBaseTab<ITabReport | ITabDashboard>): number {
    const currentEntities = Object.values(state.entities) as Array<IBaseTab<ITabReport | ITabDashboard>>;
    if ((currentEntities.length === 1 && currentEntities[0].tabId === tab.tabId) || tab.tabId === 'HOME') {
        return 0;
    }
    return currentEntities.length - 1;
}

export const reportTabsReducer = createReducer(
    reportTabsInitialState,
    on(ReportTabActions.addTabAction, (state, { tab }) => {
        const addState = reportTabAdapter.addOne(tab, state);
        const index = calculateTabIndex(addState, tab);
        const tabInfo = {
            ...state.tabInfo,
            [tab.tabId]: {
                ...state.tabInfo[tab.tabId],
                unsaved: false,
                index: index,
                invalid: true // set to true as load begins, because we haven't received data
            }
        };
        return {
            ...addState,
            selectedReportTabId: tab.tabId,
            tabInfo
        };
    }),
    on(ReportTabActions.closeTabAction, (state, { id }) => {
        let updatedTabInfo: { [k: string]: ITabInfo } = {};
        for (const key in state.tabInfo) {
            if (key !== id) {
                updatedTabInfo = {
                    ...updatedTabInfo,
                    [key]: state.tabInfo[key]
                };
            }
        }
        for (const [ind, [key, value]] of Object.entries(updatedTabInfo).entries()) {
            updatedTabInfo = {
                ...updatedTabInfo,
                [key]: {
                    ...value,
                    index: ind
                }
            };
        }
        let orderedIds = Object.keys(state.tabInfo);
        if (orderedIds && orderedIds.length > 0 && id === state.selectedReportTabId) {
            // Check next default index of tab
            let activeIndex = (<string[]>orderedIds).indexOf(id);
            activeIndex = activeIndex > 0 ? activeIndex - 1 : activeIndex + 1;

            // Check if there only two open tabs after "Closing others" event
            if (Object.values(updatedTabInfo).length == 2) {
                activeIndex = Object.values(updatedTabInfo).find(elem => elem.index > 0)?.index as number;
                orderedIds = Object.keys(updatedTabInfo);
            }

            // Check if tab is set to close in "Closing others" event loop
            const isSetToClose = Object.values(updatedTabInfo).find(elem => elem.toClose);
            if (isSetToClose) {
                activeIndex = isSetToClose.index;
            }

            state = {
                ...state,
                selectedReportTabId: orderedIds[activeIndex]
            };
        }
        return reportTabAdapter.removeOne(id, { ...state, tabInfo: updatedTabInfo });
    }),
    on(ReportTabActions.switchTabAction, (state, { id }) => {
        return { ...state, selectedReportTabId: id };
    }),
    on(ReportTabActions.switchToLastTab, state => {
        const orderedIds = Object.keys(state.tabInfo);
        const switchId = orderedIds[orderedIds.length - 1];
        return { ...state, selectedReportTabId: switchId };
    }),
    on(ReportTabActions.setToClosed, (state, { id }) => {
        const tabInfo = {
            ...state.tabInfo,
            [id]: {
                ...state.tabInfo[id as string],
                toClose: true
            }
        };
        return { ...state, tabInfo };
    }),
    on(ReportTabActions.dataLoadedTabAction, (state, { reportUUID, name }) => {
        const tabInfo = {
            ...state.tabInfo,
            [reportUUID]: {
                ...state.tabInfo[reportUUID as string],
                unsaved: true,
                name: name,
                invalid: false // set to false now that we have received data
            }
        };
        return {
            ...state,
            selectedReportTabId: reportUUID,
            tabInfo
        };
    }),
    on(ReportTabActions.reportLoadedByIdTabAction, (state, { reportUUID, name }) => {
        const tabInfo = {
            ...state.tabInfo,
            [reportUUID]: {
                ...state.tabInfo[reportUUID as string],
                unsaved: false,
                name: name,
                invalid: false // set to false now that we have received data
            }
        };
        return {
            ...state,
            selectedReportTabId: reportUUID,
            tabInfo
        };
    }),
    on(ReportTabActions.savedTabAction, (state, { name, reportUUID }) => {
        const tabId = reportUUID || state.selectedReportTabId;
        const tabInfo = {
            ...state.tabInfo,
            [tabId]: {
                ...state.tabInfo[tabId as string],
                unsaved: false,
                name: name
            }
        };
        // removed for bug PFD-4905
        // changing the entities makes the tabs redraw, causing content to destroy
        // let updatedState = state;
        // if (state.entities[reportUUID] && state.entities[reportUUID]?.content) {
        //     updatedState = reportTabAdapter.updateOne({ id: String(reportUUID) , changes: {
        //         ...state.entities[reportUUID],
        //         content: {
        //             ...state.entities[reportUUID]?.content,
        //             name: name
        //         }
        //     }}, state)
        // }
        return {
            ...state,
            selectedReportTabId: tabId,
            tabInfo
        };
    }),
    on(ReportTabActions.setUnsavedTabAction, (state, { reportUUID }) => {
        const tabInfo = {
            ...state.tabInfo,
            [reportUUID]: {
                ...state.tabInfo[reportUUID as string],
                unsaved: true
            }
        };
        return {
            ...state,
            selectedReportTabId: reportUUID,
            tabInfo
        };
    }),
    on(ReportTabActions.updateTabInfoData, (state, { reportId, reportUUID }) => {
        const tabInfo = {
            ...state.tabInfo,
            [reportUUID]: {
                ...state.tabInfo[reportUUID as string],
                id: reportId
            }
        };
        return {
            ...state,
            tabInfo
        };
    })
);
